import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { MailWarning, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Soft email-verification nudge.
 * Only renders when the logged-in user has email_verified === false.
 * Lets the user resend the verification email.
 */
const EmailVerificationBanner = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);

  if (!user || user.email_verified || dismissed) return null;

  const handleResend = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/api/auth/resend-verification`, null, {
        withCredentials: true,
      });
      toast.success(t('emailVerify.sentToast'));
    } catch (e) {
      toast.error(
        e.response?.data?.detail || t('emailVerify.sendFailToast')
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="email-verification-banner"
      className="bg-amber-50 border-b border-amber-200 px-4 py-3"
    >
      <div className="max-w-7xl mx-auto flex items-center gap-3">
        <MailWarning className="h-5 w-5 text-amber-700 flex-shrink-0" />
        <p className="text-sm text-amber-900 flex-1">
          {user?.email
            ? t('emailVerify.bannerWithEmail', { email: user.email })
            : t('emailVerify.bannerNoEmail')}
        </p>
        <button
          data-testid="resend-verification-btn"
          onClick={handleResend}
          disabled={sending}
          className="text-sm font-medium text-amber-900 underline underline-offset-2 hover:text-amber-700 disabled:opacity-60"
        >
          {sending ? t('emailVerify.sending') : t('emailVerify.resend')}
        </button>
        <button
          data-testid="dismiss-verification-banner-btn"
          onClick={() => setDismissed(true)}
          className="text-amber-700 hover:text-amber-900"
          aria-label={t('common.close')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
